import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MoleculeGraph, Hexagon, FloatingShape, AnimatedDnaHelix } from "../Molecules";
import { Overline } from "../Reveal";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (window.lenis) window.lenis.scrollTo(el, { duration: 1.4 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yFar = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yMid = useTransform(scrollYProgress, [0, 1], [0, -260]);
  const yNear = useTransform(scrollYProgress, [0, 1], [0, -420]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // mouse-follow 3D tilt for the helix
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-26, 26]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [18, -18]), {
    stiffness: 120,
    damping: 18,
  });

  const handleMouse = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={handleMouse}
      onMouseLeave={resetTilt}
      className="relative min-h-screen flex items-center overflow-hidden bg-white"
    >
      {/* grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(9,9,11,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(9,9,11,0.045) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* floating parallax molecules */}
      <motion.div style={{ y: yFar }} className="absolute top-[18%] right-[8%] hidden sm:block">
        <FloatingShape duration={9}>
          <MoleculeGraph className="w-40 h-40 lg:w-56 lg:h-56 blur-[0.4px] opacity-30" />
        </FloatingShape>
      </motion.div>

      <motion.div style={{ y: yMid }} className="absolute bottom-[14%] left-[6%] hidden md:block">
        <FloatingShape duration={11} delay={1}>
          <Hexagon className="w-24 h-24 opacity-40" />
        </FloatingShape>
      </motion.div>

      <motion.div style={{ y: yNear }} className="absolute right-[5%] bottom-0 top-0 hidden lg:flex items-center">
        <motion.div
          style={{ rotateX, rotateY, transformPerspective: 1200 }}
          className="[transform-style:preserve-3d]"
        >
          <AnimatedDnaHelix rungs={24} className="h-[78vh]" />
        </motion.div>
      </motion.div>

      <motion.div style={{ y: yFar }} className="absolute top-[30%] left-[42%] hidden lg:block">
        <FloatingShape duration={13} delay={0.5}>
          <Hexagon className="w-12 h-12 opacity-25" stroke="#09090b" />
        </FloatingShape>
      </motion.div>

      {/* content */}
      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <Overline>Precision genetic medicine</Overline>
        </motion.div>

        <h1
          aria-label="Rewriting biology to cure the incurable."
          className="font-heading font-light text-5xl sm:text-6xl lg:text-8xl tracking-tighter text-zinc-900 mt-6 max-w-4xl leading-[0.95]"
        >
          {["Rewriting biology", "to cure the", "incurable."].map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE }}
              >
                {i === 2 ? (
                  <>
                    incura<span className="text-brand">ble.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
          className="font-body text-base lg:text-lg leading-relaxed text-zinc-600 mt-8 max-w-xl"
        >
          BioSoma engineers programmable therapeutics at the level of the genome —
          combining gene editing, AI-driven discovery and precision delivery to treat
          disease at its source.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("pipeline")}
            className="inline-flex items-center gap-2 font-body text-sm font-semibold text-white bg-brand hover:bg-brand-hover px-7 py-3.5 rounded-full transition-colors"
          >
            Explore our pipeline
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("technology")}
            className="inline-flex items-center gap-2 font-body text-sm font-semibold text-zinc-900 border border-black/15 hover:border-brand hover:text-brand px-7 py-3.5 rounded-full transition-colors"
          >
            The science
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-3 text-zinc-400"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-mono text-xs tracking-widest uppercase">Scroll</span>
      </motion.div>
    </section>
  );
}
